import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Replay as ReplayIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import api from '../services/api';

interface QueueStats {
  listingPosting?: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
  contract?: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
  unified?: {
    waiting: number;
    active: number;
    completed: number;
    failed: number;
    delayed: number;
  };
}

interface QueueJob {
  jobId: string;
  jobType: string;
  status: string;
  propertyId?: string;
  agencyId?: string;
  createdAt: string;
  processedAt?: string;
  finishedAt?: string;
  attemptsMade: number;
  progress?: number;
  result?: any;
  error?: string;
  data?: any;
}

const AdminQueueManagement = () => {
  const [stats, setStats] = useState<QueueStats>({});
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [selectedQueue, setSelectedQueue] = useState<string>('listing-posting');
  const [selectedJobType, setSelectedJobType] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [jobTypes, setJobTypes] = useState<string[]>([]);
  const [selectedJob, setSelectedJob] = useState<QueueJob | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [retrying, setRetrying] = useState<string | null>(null);
  const [diagnostics, setDiagnostics] = useState<any>(null);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  useEffect(() => {
    fetchStats();
    fetchJobTypes();
  }, [selectedQueue]);

  useEffect(() => {
    fetchJobs();
  }, [selectedQueue, selectedJobType, selectedStatus]);

  const fetchStats = async () => {
    try {
      const response = await api.get(`/admin/queues/stats?queueType=${selectedQueue}`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching queue stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiagnostics = async () => {
    try {
      const response = await api.get(`/admin/queues/${selectedQueue}/diagnostics`);
      setDiagnostics(response.data);
      setShowDiagnostics(true);
    } catch (error: any) {
      console.error('Error fetching diagnostics:', error);
      alert(`Failed to fetch diagnostics: ${error.response?.data?.message || error.message}`);
    }
  };

  const fetchJobTypes = async () => {
    try {
      const response = await api.get(`/admin/queues/${selectedQueue}/job-types`);
      setJobTypes(response.data.jobTypes || []);
    } catch (error) {
      console.error('Error fetching job types:', error);
    }
  };

  const fetchJobs = async () => {
    try {
      setJobsLoading(true);
      const params = new URLSearchParams();
      if (selectedJobType) params.append('jobType', selectedJobType);
      if (selectedStatus) params.append('status', selectedStatus);
      params.append('limit', '100');
      
      const response = await api.get(`/admin/queues/${selectedQueue}/jobs?${params.toString()}`);
      setJobs(response.data.jobs || []);
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setJobsLoading(false);
    }
  };

  const handleRetryJob = async (jobId: string) => {
    try {
      setRetrying(jobId);
      await api.post(`/admin/queues/${selectedQueue}/jobs/${jobId}/retry`);
      await fetchJobs();
      await fetchStats();
      alert('Job retried successfully');
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message;
      if (errorMsg.includes('Invalid queue type') || errorMsg.includes('not supported')) {
        alert(`Retry is only supported for listing-posting queue. Current queue: ${selectedQueue}`);
      } else {
        alert(`Failed to retry job: ${errorMsg}`);
      }
    } finally {
      setRetrying(null);
    }
  };

  const handleViewJob = (job: QueueJob) => {
    setSelectedJob(job);
    setDetailDialogOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'active':
        return 'info';
      case 'waiting':
        return 'warning';
      case 'delayed':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-AU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const currentStats = selectedQueue === 'listing-posting' 
    ? stats.listingPosting 
    : selectedQueue === 'contract' 
    ? stats.contract 
    : stats.unified;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Queue Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Monitor and manage background job queues
        </Typography>
        {selectedQueue === 'listing-posting' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Listing Posting Jobs</strong> are created when:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>An agency completes the "Create Listing" task</li>
              <li>The agency has a subscribed plan (not Free/Trial)</li>
              <li>The agency has at least one integration configured (Realestate.com.au or Domain.com.au)</li>
            </ul>
            <Typography variant="body2" sx={{ mt: 1 }}>
              Jobs are automatically retried until successful (with 30+ minute delays between retries).
            </Typography>
          </Alert>
        )}
      </Box>

      {/* Queue Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Queue Type</InputLabel>
              <Select
                value={selectedQueue}
                label="Queue Type"
                onChange={(e) => setSelectedQueue(e.target.value)}
              >
                <MenuItem value="listing-posting">Listing Posting</MenuItem>
                <MenuItem value="contract">Contract</MenuItem>
                <MenuItem value="unified">Unified Notification</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={selectedJobType}
                label="Job Type"
                onChange={(e) => setSelectedJobType(e.target.value)}
              >
                <MenuItem value="">All Types</MenuItem>
                {jobTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e) => setSelectedStatus(e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                <MenuItem value="waiting">Waiting</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
                <MenuItem value="delayed">Delayed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => {
              fetchStats();
              fetchJobs();
            }}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            onClick={fetchDiagnostics}
          >
            Show Diagnostics
          </Button>
        </Box>
      </Paper>

      {/* Diagnostics */}
      {showDiagnostics && diagnostics && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'info.light', color: 'info.contrastText' }}>
          <Typography variant="h6" gutterBottom>
            Queue Diagnostics
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Redis Connected:</strong> {diagnostics.redisConnected ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Redis Status:</strong> {diagnostics.redisStatus}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2">
                <strong>Remove On Complete:</strong> {diagnostics.config?.removeOnComplete}
              </Typography>
              <Typography variant="body2">
                <strong>Remove On Fail:</strong> {diagnostics.config?.removeOnFail ? 'No (kept)' : 'Yes'}
              </Typography>
              <Typography variant="body2">
                <strong>Max Attempts:</strong> {diagnostics.config?.maxAttempts}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" component="pre" sx={{ bgcolor: 'rgba(0,0,0,0.1)', p: 1, borderRadius: 1, fontSize: '0.75rem' }}>
                {JSON.stringify(diagnostics, null, 2)}
              </Typography>
            </Grid>
          </Grid>
          <Button
            size="small"
            onClick={() => setShowDiagnostics(false)}
            sx={{ mt: 2 }}
          >
            Hide Diagnostics
          </Button>
        </Paper>
      )}

      {/* Statistics Cards */}
      {currentStats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Waiting
                </Typography>
                <Typography variant="h4">{currentStats.waiting || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active
                </Typography>
                <Typography variant="h4">{currentStats.active || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Completed
                </Typography>
                <Typography variant="h4">{currentStats.completed || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Failed
                </Typography>
                <Typography variant="h4" color="error">
                  {currentStats.failed || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Delayed
                </Typography>
                <Typography variant="h4">{currentStats.delayed || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Jobs Table */}
      <Paper>
        <TableContainer>
          <Table>
              <TableHead>
              <TableRow>
                <TableCell>Job ID</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                {selectedQueue === 'listing-posting' && (
                  <>
                    <TableCell>Property ID</TableCell>
                    <TableCell>Agency ID</TableCell>
                  </>
                )}
                {(selectedQueue === 'contract' || selectedQueue === 'unified') && (
                  <TableCell>Data</TableCell>
                )}
                <TableCell>Attempts</TableCell>
                <TableCell>Progress</TableCell>
                <TableCell>Created At</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {jobsLoading ? (
                <TableRow>
                  <TableCell colSpan={selectedQueue === 'listing-posting' ? 9 : 8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : jobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={selectedQueue === 'listing-posting' ? 9 : 8} align="center">
                    No jobs found
                  </TableCell>
                </TableRow>
              ) : (
                jobs.map((job) => (
                  <TableRow key={job.jobId}>
                    <TableCell>{job.jobId}</TableCell>
                    <TableCell>{job.jobType}</TableCell>
                    <TableCell>
                      <Chip
                        label={job.status}
                        color={getStatusColor(job.status) as any}
                        size="small"
                      />
                    </TableCell>
                    {selectedQueue === 'listing-posting' && (
                      <>
                        <TableCell>{job.propertyId || 'N/A'}</TableCell>
                        <TableCell>{job.agencyId || 'N/A'}</TableCell>
                      </>
                    )}
                    {(selectedQueue === 'contract' || selectedQueue === 'unified') && (
                      <TableCell>
                        {job.data ? (
                          <Tooltip title={JSON.stringify(job.data, null, 2)}>
                            <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {JSON.stringify(job.data).substring(0, 50)}...
                            </Typography>
                          </Tooltip>
                        ) : 'N/A'}
                      </TableCell>
                    )}
                    <TableCell>{job.attemptsMade}</TableCell>
                    <TableCell>
                      {job.progress !== undefined ? `${job.progress}%` : 'N/A'}
                    </TableCell>
                    <TableCell>{formatDate(job.createdAt)}</TableCell>
                    <TableCell>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewJob(job)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                      {job.status === 'failed' && selectedQueue === 'listing-posting' && (
                        <Tooltip title="Retry Job">
                          <IconButton
                            size="small"
                            onClick={() => handleRetryJob(job.jobId)}
                            disabled={retrying === job.jobId}
                          >
                            <ReplayIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Job Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Job Details</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Job ID
                  </Typography>
                  <Typography variant="body1">{selectedJob.jobId}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Job Type
                  </Typography>
                  <Typography variant="body1">{selectedJob.jobType}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Status
                  </Typography>
                  <Chip
                    label={selectedJob.status}
                    color={getStatusColor(selectedJob.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Attempts Made
                  </Typography>
                  <Typography variant="body1">{selectedJob.attemptsMade}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Created At
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedJob.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Processed At
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedJob.processedAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Finished At
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedJob.finishedAt)}</Typography>
                </Grid>
                {selectedJob.propertyId && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Property ID
                    </Typography>
                    <Typography variant="body1">{selectedJob.propertyId}</Typography>
                  </Grid>
                )}
                {selectedJob.agencyId && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Agency ID
                    </Typography>
                    <Typography variant="body1">{selectedJob.agencyId}</Typography>
                  </Grid>
                )}
                {selectedJob.error && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Error
                    </Typography>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {selectedJob.error}
                    </Alert>
                  </Grid>
                )}
                {selectedJob.result && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Result
                    </Typography>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedJob.result, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
                {selectedJob.data && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Job Data
                    </Typography>
                    <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                      <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                        {JSON.stringify(selectedJob.data, null, 2)}
                      </pre>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          {selectedJob?.status === 'failed' && (
            <Button
              variant="contained"
              onClick={() => {
                if (selectedJob) {
                  handleRetryJob(selectedJob.jobId);
                  setDetailDialogOpen(false);
                }
              }}
              disabled={retrying === selectedJob?.jobId}
            >
              Retry Job
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminQueueManagement;

